import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Pencil, Plus } from "lucide-react";

import {
  planCatalogApi,
  planCatalogQueryKeys,
  type PlanCatalogRow,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlanCreateDialog, PlanEditDialog } from "./PlanFormDialogs";
import { PlanScreensDialog } from "./PlanScreensPanel";

const VND = new Intl.NumberFormat("vi-VN");

/** Hộp thoại đang mở. Một giá trị duy nhất, nên không thể có hai biểu mẫu mở chồng lên nhau. */
type Overlay =
  | { kind: "create" }
  | { kind: "edit"; planId: string }
  | { kind: "features"; planId: string };

/**
 * Bảng giá gói dịch vụ (#18990).
 *
 * <p>Tên gói và giá vốn đã nằm trong bảng {@code subscription_plans} — không hard code — nhưng
 * không có màn hình nào sửa được, nên đổi giá phải qua SQL hoặc một migration. Còn <b>gói nào bán
 * được</b> thì thật sự hard code: một {@code Set.of("BASIC","PRO")} trong Java, lặp ở hai nơi
 * khác nhau. Ngừng bán một gói là quyết định kinh doanh, không nên cần một lần build.
 *
 * <p><b>Đổi giá không hồi tố.</b> Kỳ đã bán giữ nguyên giá lúc bán, và giá thoả thuận riêng của
 * từng phòng khám vẫn thắng giá niêm yết. Sửa ở đây chỉ ảnh hưởng lần mua tiếp theo của phòng
 * khám không có giá riêng.
 *
 * <p><b>Gói gồm những tính năng nào cũng sửa ở đây</b> (ADR-0053): nút "Tính năng" mở bảng tick, và
 * "Tạo gói" thêm một gói mới — giá 0đ là gói miễn phí.
 */
export function PlatformPlanCatalogPage() {
  const qc = useQueryClient();
  const [overlay, setOverlay] = useState<Overlay | null>(null);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: planCatalogQueryKeys.all(),
    queryFn: () => planCatalogApi.list(),
  });

  const toggle = useMutation({
    mutationFn: (v: { id: string; sellable: boolean }) =>
      planCatalogApi.update(v.id, { sellable: v.sellable }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: planCatalogQueryKeys.all() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const rows = q.data ?? [];
  const overlayPlan =
    overlay && overlay.kind !== "create" ? rows.find((r) => r.id === overlay.planId) : undefined;

  const close = () => setOverlay(null);
  const closeAndRefresh = () => {
    close();
    void qc.invalidateQueries({ queryKey: planCatalogQueryKeys.all() });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">Gói dịch vụ &amp; bảng giá</h1>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            Giá niêm yết cho phòng khám <b>không có giá thoả thuận riêng</b>. Sửa ở đây{" "}
            <b>không hồi tố</b>: kỳ đã bán giữ nguyên giá lúc bán, và giá riêng theo hợp đồng vẫn
            thắng giá niêm yết.
          </p>
        </div>
        <Button size="sm" onClick={() => setOverlay({ kind: "create" })}>
          <Plus />
          Tạo gói
        </Button>
      </div>

      {(q.isError || error) && (
        <p className="text-sm text-destructive">{error ?? apiError(q.error).detail}</p>
      )}
      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}

      {q.data && (
        <Card className="overflow-hidden">
          <Table className="min-w-[52rem]">
            <TableHeader>
              <TableRow>
                <TableHead>Gói</TableHead>
                <TableHead className="text-right">Giá / tháng</TableHead>
                <TableHead>Trạng thái</TableHead>
                <TableHead className="text-right">Tính năng</TableHead>
                <TableHead className="text-right">Phòng khám đang dùng</TableHead>
                <TableHead className="text-right">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Chưa có gói nào. Nhấn “Tạo gói” để bắt đầu.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  busy={toggle.isPending}
                  onFeatures={() => setOverlay({ kind: "features", planId: plan.id })}
                  onEdit={() => setOverlay({ kind: "edit", planId: plan.id })}
                  onToggleSellable={() => toggle.mutate({ id: plan.id, sellable: !plan.sellable })}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ul className="max-w-prose list-disc space-y-1 pl-4 text-xs text-muted-foreground">
        <li>
          <b>Ngừng bán</b> chỉ chặn mua mới — phòng khám đang ở gói đó vẫn dùng hết kỳ đã trả tiền.
        </li>
        <li>
          Gói <b>0đ đang mở</b> là gói phòng khám tự <b>chọn</b> được mà không qua ví: nó không hết
          hạn, và là đường ra khỏi chế độ chỉ xem cho phòng khám không muốn trả tiền.
        </li>
      </ul>

      {overlay?.kind === "create" && (
        <PlanCreateDialog onCreated={closeAndRefresh} onClose={close} />
      )}
      {overlay?.kind === "edit" && overlayPlan && (
        <PlanEditDialog plan={overlayPlan} onSaved={closeAndRefresh} onClose={close} />
      )}
      {/* key: đổi sang gói khác là một bảng tick mới, không mang bản nháp của gói trước sang. */}
      {overlay?.kind === "features" && overlayPlan && (
        <PlanScreensDialog key={overlayPlan.id} plan={overlayPlan} onClose={close} />
      )}
    </div>
  );
}

function PlanRow({
  plan,
  busy,
  onFeatures,
  onEdit,
  onToggleSellable,
}: {
  plan: PlanCatalogRow;
  busy: boolean;
  onFeatures: () => void;
  onEdit: () => void;
  onToggleSellable: () => void;
}) {
  const inUse = plan.tenantsOnPlan > 0;
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-foreground">{plan.name}</div>
        <div className="font-mono text-xs text-muted-foreground">{plan.code}</div>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {plan.monthlyPriceVnd > 0 ? (
          `${VND.format(plan.monthlyPriceVnd)}đ`
        ) : (
          <span className="text-muted-foreground">Miễn phí</span>
        )}
      </TableCell>
      <TableCell>
        {plan.sellable ? (
          <Badge variant="success" className="dark:bg-emerald-950/60 dark:text-emerald-400">
            Đang bán
          </Badge>
        ) : (
          <Badge variant="muted">Không bán</Badge>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{plan.screenCount}</TableCell>
      <TableCell className="text-right tabular-nums">
        {inUse ? (
          <b className="text-foreground">{plan.tenantsOnPlan}</b>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={onFeatures}>
            <ListChecks />
            Tính năng
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil />
            Sửa
          </Button>
          {/* Rộng cố định: "Mở bán" và "Ngừng bán" dài khác nhau, không thì cột nút so le. */}
          <Button
            size="sm"
            variant="ghost"
            className="w-24 text-muted-foreground"
            disabled={busy}
            title={
              plan.sellable && inUse
                ? `${plan.tenantsOnPlan} phòng khám đang ở gói này — ngừng bán không đuổi ai ra, chỉ chặn mua mới`
                : undefined
            }
            onClick={onToggleSellable}
          >
            {plan.sellable ? "Ngừng bán" : "Mở bán"}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
